import { just } from 'maybeasy';
import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import Main from '../../BrowserSupport/Main';
import { currentUserStore } from '../../CurrentUserStore';
import { emptyFragment } from '../../EmptyFragment';
import { findLink } from '../../LinkyLinky';
import Notifications from '../../Notifications';
import I18nTitle from '../../Title/I18nTitle';
import MainNav from '../Platform/MainNav';
import Toast from '../Toast';
import { ToastContext } from '../Toast/Context';
import ToastStore from '../Toast/Store';
import CountStore from './CountStore';
import { countGroups, countParticipants } from './CountStore/Load';
import CountReactions from './CountStore/Reactions';
import TitleRow from './TitleRow';

/**
 * The redesigned People page, mounted at /people for admins with the
 * `people-v2` link (DVLP-7894). The page owns the shared toast slot and
 * provides it through ToastContext, so the later Phase 1 cards (selection
 * tray, person drawer) can show toasts from inside the route.
 */
function PeopleV2() {
  const participantsCount = useMemo(() => new CountStore(), []);
  const groupsCount = useMemo(() => new CountStore(), []);
  const toastStore = useMemo(() => new ToastStore(), []);

  // Only count groups when the Groups segment can render; without the link
  // the request can only fail.
  const groupsCountReactions = findLink('user-groups', currentUserStore.links)
    .map(() => <CountReactions store={groupsCount} count={countGroups} fireImmediately />)
    .getOrElse(emptyFragment);

  return (
    <>
      <I18nTitle title="People" />
      <div className="min-h-screen bg-background">
        <MainNav />
        <Notifications />
        <div className="mx-auto w-full max-w-clientPortalMaxWidth pb-16 pt-10">
          <Main id="main" className="min-h-[calc(100vh-150px)]">
            <TitleRow participantsCount={participantsCount} groupsCount={groupsCount} />
            <ToastContext.Provider value={just(toastStore)}>
              <div className="mt-8">
                <Outlet />
              </div>
            </ToastContext.Provider>
          </Main>
        </div>
        <Toast store={toastStore} />
        <CountReactions store={participantsCount} count={countParticipants} fireImmediately />
        {groupsCountReactions}
      </div>
    </>
  );
}

export default observer(PeopleV2);
